import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, reduxForm } from 'redux-form';
import { Collapse } from 'reactstrap';
import { withRouter } from 'react-router-dom';
import GoogleMapReact from 'google-map-react';
import queryString from 'query-string';
import trans from 'counterpart';
import config from 'config';
import SelectField from './SelectField';
import { getAvailableObjects } from '../actions';
import { RESORT_RESORT, RESORT_HOLIDAYPARK } from '../constants';
import { getObjectPreferenceInReservation, getAvailableObjectsForReservation, getResort } from '../selectors';
import { getObjects } from '../../booking/actions';
import { getInitialPropertyPreferences } from '../../../selectors/preferenceSelectors';

class ObjectPreference extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showMap: true,
    };

    this.getTileUrl = this.getTileUrl.bind(this);
  }

  /**
   * Fetch the available objects
   */
  componentDidMount() {
    this.props.fetchObjects();
  }

  componentDidUpdate = (prevProps) => {
    if (!prevProps.hasFetchedInitialReservation && this.props.hasFetchedInitialReservation) {
      const qs = queryString.parse(this.props.location.search);
      if (qs.resource) {
        this.props.fetchAvailableObjects(qs.resource);
      }
    }
  }

  /**
   * Transform the objects array to a key => value object so we can use it as options
   * in the select field
   */
  getSelectOptions = () => [{ key: '0', value: trans('preferences.no_preference') }]
    .concat(this.props.objects
      .sort((a, b) => {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      })
      .map(obj => ({
        key: obj.id || obj.key,
        value: obj.name
      })));

  /**
   * Get the URL for a google maps tile
   *
   * @param {object} coord
   * @param {number} zoom
   */
  getTileUrl(coord, zoom) {
    const { resort } = this.props;

    const locale = trans('locale');

    switch (resort) {
      case RESORT_RESORT: {
        if (zoom === 16 && (coord.x < 33700 || coord.x > 33702 || coord.y < 21786 || coord.y > 21788)) {
          return null;
        }

        if (zoom === 17 && (coord.x < 67400 || coord.x > 67405 || coord.y < 43573 || coord.y > 43577)) {
          return null;
        }

        if (zoom === 18 && (coord.x < 134801 || coord.x > 134811 || coord.y < 87146 || coord.y > 87155)) {
          return null;
        }

        return `https://${config.mediaHost}/map/v3/resort_${locale}/${zoom}/${coord.x}/${coord.y}.png`;
      }
      case RESORT_HOLIDAYPARK: {
        if (zoom === 16 && (coord.x < 33699 || coord.x > 33703 || coord.y < 21786 || coord.y > 21789)) {
          return null;
        }

        if (zoom === 17 && (coord.x < 67399 || coord.x > 67407 || coord.y < 43572 || coord.y > 43578)) {
          return null;
        }

        if (zoom === 18 && (coord.x < 134799 || coord.x > 134815 || coord.y < 87145 || coord.y > 87156)) {
          return null;
        }

        return `https://${config.mediaHost}/map/v3/holidaypark_${locale}/${zoom}/${coord.x}/${coord.y}.png`;
      }
      default: {
        return null;
      }
    }
  }

  /**
   * Get the center of the map on initialisation
   */
  getDefaultCenter() {
    const { resort } = this.props;

    switch (resort) {
      case RESORT_RESORT: {
        return { lat: 51.523175, lng: 5.128680 };
      }
      default: {
        return { lat: 51.523311, lng: 5.129992 };
      }
    }
  }

  /**
   * Get the initial zoom level
   */
  getDefaultZoom() {
    const { resort } = this.props;

    switch (resort) {
      case RESORT_HOLIDAYPARK: {
        return 16;
      }
      default: {
        return 17;
      }
    }
  }

  /**
   * Get the translated resort name
   */
  getResortName() {
    const { resort } = this.props;

    switch (resort) {
      case RESORT_RESORT: {
        return trans('resort.safari_resort');
      }
      case RESORT_HOLIDAYPARK: {
        return trans('resort.vakantiepark');
      }
      default: {
        return null;
      }
    }
  }

  /**
   * Get the boundaries of the map
   * Users should not be able to scroll outside this area
   */
  getBoundaries(maps) {
    const { resort } = this.props;

    switch (resort) {
      case RESORT_RESORT: {
        // MapTiler geolocation bounds: 5.11859755 51.52237506 5.13249730 51.52861033
        return new maps.LatLngBounds(
          new maps.LatLng(51.519475, 5.121313),
          new maps.LatLng(51.526874, 5.136046)
        );
      }
      default: {
        // MapTiler geolocation bounds; 5.11962747 51.51867718 5.14035755 51.52794562
        return new maps.LatLngBounds(
          new maps.LatLng(51.518677, 5.119627),
          new maps.LatLng(51.527946, 5.140357)
        );
      }
    }
  }

  /**
   * Render the object preference selector and the map
   */
  render() {
    const { objectInReservation, resort, hasFetchedInitialReservation } = this.props;

    // Only start loading the component when we know on what resort the user is trying make the reservation
    if (!resort || !hasFetchedInitialReservation) {
      return null;
    }

    return (
      <div className="w-100 d-block">
        <div className="col-12 pl-0 pr-0">
          <div className="col-6 d-inline-flex">
            <strong>
              {objectInReservation
                ? trans('preferences.object_selected', { object: objectInReservation.name })
                : trans('preferences.select_object')}
            </strong>
          </div>
          <div className="col-6 d-inline-flex object-preference-field">
            <Field
              name="preference-object"
              component={SelectField}
              options={this.getSelectOptions()}
            />
          </div>
          <div className="booking-map col-12 pb-3">
            <a className="booking-map-btn d-block" onClick={() => this.setState(prevState => ({ showMap: !prevState.showMap }))}>
              <strong className="title-booking-map text-white">
                {trans('preferences.show_map', { resort: this.getResortName() })}
              </strong>
              <div className="arrow float-right text-white">
                <i className="material-icons md-36 float-right">
                  {this.state.showMap ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                </i>
              </div>
            </a>
            <Collapse isOpen={this.state.showMap}>
              <div className="object-preference-map">
                <GoogleMapReact
                  bootstrapURLKeys={{
                    key: 'AIzaSyDjwj0sCmErDcVSMoJ25_mka8N9SDqy83U',
                  }}
                  onGoogleApiLoaded={({ map, maps }) => {
                    const allowedBounds = this.getBoundaries(maps);
                    let lastValidCenter = map.getCenter();

                    // Make sure the user cannot drag outside of the defined boundaries
                    maps.event.addListener(map, 'center_changed', () => {
                      if (allowedBounds.contains(map.getCenter())) {
                        // still within valid bounds, so save the last valid position
                        lastValidCenter = map.getCenter();
                        return;
                      }

                      // not valid anymore => return to last valid position
                      map.panTo(lastValidCenter);
                    });
                    map.mapTypes.set('roadmap', new maps.ImageMapType({
                      getTileUrl: this.getTileUrl,
                      tileSize: new maps.Size(256, 256),
                      name: 'Beeksebergen',
                      opacity: 1,
                      maxZoom: 18,
                      minZoom: 16,
                      radius: 1738000,
                    }));
                  }}
                  options={{
                    // tilt: 45,
                  }}
                  yesIWantToUseGoogleMapApiInternals
                  defaultCenter={this.getDefaultCenter()}
                  defaultZoom={this.getDefaultZoom()}
                />
              </div>
            </Collapse>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  resort: getResort(state),
  objects: getAvailableObjectsForReservation(state),
  objectInReservation: getObjectPreferenceInReservation(state),
  initialValues: getInitialPropertyPreferences(state),
  hasFetchedInitialReservation: state.reservation.initialFetch
});

const mapDispatchToProps = dispatch => ({
  fetchAvailableObjects: resource => dispatch(getAvailableObjects(null, resource)),
  fetchObjects: () => dispatch(getObjects())
});

ObjectPreference.propTypes = {
  fetchAvailableObjects: PropTypes.func,
  fetchObjects: PropTypes.func,
  hasFetchedInitialReservation: PropTypes.bool,
  location: PropTypes.object,
  objects: PropTypes.array,
  objectInReservation: PropTypes.object,
  resort: PropTypes.string,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(reduxForm({
  form: 'preferences'
})(withRouter(ObjectPreference)));
